import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  useGetAllMeterCycle,
  useGetAllMeterCycleForMoveRoutes,
} from "@features/mx/readCycleManagement/hooks";
import { m } from "framer-motion";
import RouteMoveMessageDialog from "./routeMoveMessageDialog";

interface ReadCycleData {
  id: string;
  name: string;
  type: string;
  utilityService: string;
  routes: number;
  nextRun: string;
}

interface MoveRoutesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRoutes: string[];
  onMoveRoutes: (destinationCycleId: string) => void;
  estimatedMeters: number;
}

export const MoveRoutesDialog: React.FC<MoveRoutesDialogProps> = ({
  open,
  onOpenChange,
  selectedRoutes,
  onMoveRoutes,
  estimatedMeters,
}) => {
  // State for the selected destination read cycle
  const [selectedDestination, setSelectedDestination] = useState<string | null>(
    null
  );
  const [moveMessageDialogOpen, setMoveMessageDialogOpen] = useState(false);

  // URL params and filtering state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [currentPage, setCurrentPage] = useState(1);
  const [meterType, setMeterType] = useState("");
  const [status, setStatus] = useState("");

  const queryParams = useMemo(
    () => ({
      open,
      routeIds: selectedRoutes,
      page: currentPage,
      search: searchQuery,
      meterType: meterType,
      status: status,
    }),
    [currentPage, searchQuery, meterType, status, selectedRoutes, open]
  );

  const { data: meterCycleResponse } = useGetAllMeterCycleForMoveRoutes(
    queryParams.open,
    queryParams.routeIds,
    queryParams.page,
    queryParams.search,
    queryParams.meterType,
    queryParams.status
  );

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = meterCycleResponse?.count || 0;
    const totalPages = Math.ceil(totalItems / 10);
    const startItem = (currentPage - 1) * 10 + 1;
    const endItem = Math.min(currentPage * 10, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: 10,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [meterCycleResponse?.count, currentPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Handle read cycle selection
  const handleSelectReadCycle = (id: string) => {
    setSelectedDestination(id === selectedDestination ? null : id);
  };

  // Table columns for read cycles
  const columns: TableColumn<ReadCycleData>[] = useMemo(
    () => [
      {
        key: "meterCycleName",
        header: "Name",
        sortable: true,
      },
      {
        key: "meterTypeDisplay",
        header: "Type",
        sortable: true,
      },
      {
        key: "productCode",
        header: "Utility Service",
        sortable: true,
      },
      {
        key: "route",
        header: "Routes",
        sortable: true,
        render: (value) => value.length,
      },
      {
        key: "nextRunDate",
        header: "Next Run",
        sortable: true,
      },
      {
        key: "action",
        header: "Action",
        width: "w-24",
        sortable: false,
        render: (_, row) => (
          <Button
            variant={selectedDestination === row.id ? "default" : "outline"}
            onClick={() => handleSelectReadCycle(row.id)}
          >
            {selectedDestination === row.id ? "Selected" : "Select"}
          </Button>
        ),
      },
    ],
    [selectedDestination]
  );

  // Reset selected destination when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedDestination(null);
    }
  }, [open]);

  // Filter panel for the GenericTable
  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Read Type</label>
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All Statuses">All Statuses</SelectItem>
              <SelectItem value="Manual Meter">Manual Meter</SelectItem>
              <SelectItem value="Photo Meter">Photo Meter</SelectItem>
              <SelectItem value="Smart Meter">Smart Meter</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  return (
    <div>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex items-start justify-between">
          <div>
            <DialogTitle className="text-xl">
              Move selected routes to another read cycle
            </DialogTitle>
            <DialogDescription>
              Choose a destination read cycle. You can search and filter the
              list below. Only one destination can be selected.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <GenericTable
            data={meterCycleResponse?.results || []}
            columns={columns}
            rowKey="id"
            emptyMessage="No read cycles found matching your criteria"
            pagination={pagination}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            searchPlaceholder="Search read cycles..."
            filterPanel={filterPanel}
            filters={
              statusFilter !== "All Statuses" ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    1 filter(s) active
                  </span>
                </div>
              ) : null
            }
          />

          <div className="flex items-center text-sm mt-4">
            <div className="text-gray-700">
              Selected routes:{" "}
              <span className="font-bold">{selectedRoutes.length}</span> •
              Estimated meters moved:{" "}
              <span className="font-bold">{estimatedMeters}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => setMoveMessageDialogOpen(true)}
            disabled={!selectedDestination}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Move Routes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <RouteMoveMessageDialog
      open={moveMessageDialogOpen}
      openMoveRouteDialog={onOpenChange}
      onOpenChange={setMoveMessageDialogOpen}
      selectedRoutes={selectedRoutes}
      selectedDestination={selectedDestination}
      estimatedMeters={estimatedMeters}
    />
    </div>
  );
};
